"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";

import { recordLandingAttribution } from "@/hooks/onboarding/onboardingFunnelApi";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ensureLandingCookie } from "@/lib/analytics/landingCookie";
import { ApiError } from "@/lib/api/client";
import Logger from "@/lib/utils/logger";

/** Marks the browser as already synced, so this is one POST per member, not one per load. */
const SYNCED_STORAGE_KEY = "sukha_landing_synced";

/**
 * How long to wait before each retry while the member's account does not exist.
 *
 * The POST fires as soon as the member is signed in, usually before
 * `/onboarding/check` has called `POST /v1/user/create`, and the server answers
 * 404 until it has. That call comes within seconds, so four tries across about
 * a minute are enough; after that the next page load tries again.
 */
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000] as const;

/**
 * localStorage access that cannot take the page down with it.
 *
 * Safari in private mode throws on `setItem`, and some privacy settings make
 * the whole property throw on access. This component mounts in the root
 * layout, so an uncaught throw here would break every page — for a flag whose
 * only job is to save a redundant POST the server already ignores.
 */
function readSyncedFlag(): boolean {
  try {
    return window.localStorage.getItem(SYNCED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSyncedFlag(): void {
  try {
    window.localStorage.setItem(SYNCED_STORAGE_KEY, "true");
  } catch {
    // Storage unavailable. The server is idempotent, so the cost is one
    // repeat POST per load, not a duplicated row.
  }
}

/**
 * Captures where a member arrived from, and hands it to the server once.
 *
 * Two separate jobs, deliberately in one place so the ordering is obvious:
 * the cookie is written on every visitor including anonymous ones, and it is
 * copied to the database only once the visitor has a member id to attach it
 * to. The server ignores repeat calls, so the storage flag here is a courtesy
 * to the network rather than the thing that guarantees write-once.
 */
export function LandingAttributionTracker(): null {
  const { isLoaded, isSignedIn } = useAuth();
  const { isMobileDevice, hasResolved } = useIsMobile();
  const hasSyncedRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ensureLandingCookie();
  }, []);

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !hasResolved || hasSyncedRef.current) {
      return;
    }

    if (readSyncedFlag()) {
      hasSyncedRef.current = true;
      return;
    }

    const landing = ensureLandingCookie();

    if (!landing) {
      return;
    }

    hasSyncedRef.current = true;

    const input = {
      visitorId: landing.visitorId,
      referrerUrl: landing.referrerUrl,
      utmSource: landing.utmSource,
      utmMedium: landing.utmMedium,
      utmCampaign: landing.utmCampaign,
      utmContent: landing.utmContent,
      utmTerm: landing.utmTerm,
      landingPath: landing.landingPath,
      sourcePage: landing.sourcePage,
      sourceCta: landing.sourceCta,
      sourceReferrer: landing.sourceReferrer,
      landedAt: landing.landedAt,
      isMobileDevice,
    };

    /*
     * Marked synced only once the server has answered 200 — whether or not
     * this call created the row. It used to be marked BEFORE the POST, so the
     * first 404 (no account yet) was final and the landing was lost (TX-5059).
     */
    const attempt = (retry: number): void => {
      recordLandingAttribution(input).then(
        () => writeSyncedFlag(),
        (error: unknown) => {
          const delay = RETRY_DELAYS_MS[retry];
          if (error instanceof ApiError && error.status === 404 && delay !== undefined) {
            retryTimerRef.current = setTimeout(() => attempt(retry + 1), delay);
            return;
          }
          // Left unsynced, so the next page load tries again.
          Logger.warn("[onboardingFunnel] Failed to record landing attribution", error);
        }
      );
    };
    attempt(0);
  }, [hasResolved, isLoaded, isMobileDevice, isSignedIn]);

  return null;
}
